import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { CalendarRange, ChevronLeft, ChevronRight } from "lucide-react";

type Props = {
  from: string;
  to: string;
  disabled?: boolean;
  onChange: (from: string, to: string) => Promise<void> | void;
};

type CalendarDay = {
  date: Date;
  value: string;
  currentMonth: boolean;
};

const monthNames = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const weekDays = ["L", "M", "X", "J", "V", "S", "D"];

export function ClockingDateRangePicker({
  from,
  to,
  disabled = false,
  onChange,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [visibleMonth, setVisibleMonth] = useState(() =>
    startOfMonth(dateFromValue(from) ?? new Date()),
  );

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const selectedYears = [
      visibleMonth.getFullYear(),
      dateFromValue(from)?.getFullYear(),
      dateFromValue(to)?.getFullYear(),
    ].filter((value): value is number => typeof value === "number");
    const minimum = Math.min(currentYear - 25, ...selectedYears);
    const maximum = Math.max(currentYear + 10, ...selectedYears);

    return Array.from(
      { length: maximum - minimum + 1 },
      (_, index) => minimum + index,
    );
  }, [from, to, visibleMonth]);

  const calendarDays = useMemo(
    () => buildCalendarDays(visibleMonth),
    [visibleMonth],
  );
  const today = toDateValue(new Date());
  const canApply = Boolean(draftFrom && draftTo);

  const openCalendar = () => {
    setDraftFrom(from);
    setDraftTo(to);
    setVisibleMonth(startOfMonth(dateFromValue(from || to) ?? new Date()));
    setOpen(true);
  };

  const selectDate = (value: string) => {
    if (!draftFrom || draftTo) {
      setDraftFrom(value);
      setDraftTo("");
      return;
    }

    if (value < draftFrom) {
      setDraftFrom(value);
      setDraftTo(draftFrom);
      return;
    }

    setDraftTo(value);
  };

  const applyRange = async () => {
    if (!draftFrom || !draftTo) return;

    setOpen(false);
    await onChange(draftFrom, draftTo);
  };

  const selectToday = async () => {
    const value = toDateValue(new Date());
    setDraftFrom(value);
    setDraftTo(value);
    setOpen(false);
    await onChange(value, value);
  };

  const moveMonth = (offset: number) => {
    setVisibleMonth(
      new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + offset, 1),
    );
  };

  const handleTriggerKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openCalendar();
    }
  };

  return (
    <div className="clocking-date-range" ref={rootRef}>
      <button
        type="button"
        className="clocking-date-range-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openCalendar())}
        onKeyDown={handleTriggerKeyDown}
      >
        <CalendarRange size={17} />
        <span>{formatRange(from, to)}</span>
      </button>

      {open ? (
        <div
          className="clocking-date-range-popover"
          role="dialog"
          aria-label="Seleccionar rango de fechas de fichajes"
        >
          <div className="clocking-date-range-navigation">
            <button
              type="button"
              className="clocking-calendar-nav-button"
              aria-label="Mes anterior"
              onClick={() => moveMonth(-1)}
            >
              <ChevronLeft size={17} />
            </button>

            <div className="clocking-calendar-selectors">
              <select
                aria-label="Mes"
                value={visibleMonth.getMonth()}
                onChange={(event) =>
                  setVisibleMonth(
                    new Date(
                      visibleMonth.getFullYear(),
                      Number(event.target.value),
                      1,
                    ),
                  )
                }
              >
                {monthNames.map((month, index) => (
                  <option key={month} value={index}>
                    {month}
                  </option>
                ))}
              </select>

              <select
                aria-label="Año"
                value={visibleMonth.getFullYear()}
                onChange={(event) =>
                  setVisibleMonth(
                    new Date(
                      Number(event.target.value),
                      visibleMonth.getMonth(),
                      1,
                    ),
                  )
                }
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="clocking-calendar-nav-button"
              aria-label="Mes siguiente"
              onClick={() => moveMonth(1)}
            >
              <ChevronRight size={17} />
            </button>
          </div>

          <div className="clocking-calendar-weekdays" aria-hidden="true">
            {weekDays.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className="clocking-calendar-grid">
            {calendarDays.map((day) => {
              const isStart = day.value === draftFrom;
              const isEnd = day.value === draftTo;
              const isInRange = Boolean(
                draftFrom &&
                  draftTo &&
                  day.value > draftFrom &&
                  day.value < draftTo,
              );
              const isToday = day.value === today;

              return (
                <button
                  key={day.value}
                  type="button"
                  className={[
                    "clocking-calendar-day",
                    day.currentMonth ? "" : "is-outside",
                    isToday ? "is-today" : "",
                    isInRange ? "is-in-range" : "",
                    isStart ? "is-range-start" : "",
                    isEnd ? "is-range-end" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-label={formatAccessibleDate(day.date)}
                  aria-pressed={isStart || isEnd}
                  onClick={() => selectDate(day.value)}
                >
                  {day.date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="clocking-date-range-summary" aria-live="polite">
            <span>
              Desde <strong>{draftFrom ? formatDateValue(draftFrom) : "—"}</strong>
            </span>
            <span>
              Hasta <strong>{draftTo ? formatDateValue(draftTo) : "—"}</strong>
            </span>
          </div>

          <div className="clocking-date-range-actions">
            <button
              type="button"
              className="clocking-date-range-secondary"
              onClick={() => void selectToday()}
            >
              Hoy
            </button>
            <button
              type="button"
              className="clocking-date-range-primary"
              disabled={!canApply}
              onClick={() => void applyRange()}
            >
              Aplicar rango
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function buildCalendarDays(month: Date): CalendarDay[] {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const mondayOffset = (firstDay.getDay() + 6) % 7;
  const firstVisibleDay = new Date(
    firstDay.getFullYear(),
    firstDay.getMonth(),
    firstDay.getDate() - mondayOffset,
  );

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(
      firstVisibleDay.getFullYear(),
      firstVisibleDay.getMonth(),
      firstVisibleDay.getDate() + index,
    );

    return {
      date,
      value: toDateValue(date),
      currentMonth: date.getMonth() === month.getMonth(),
    };
  });
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function dateFromValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

  if (!match) return null;

  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

function toDateValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateValue(value: string): string {
  const date = dateFromValue(value);
  return date ? new Intl.DateTimeFormat("es-ES").format(date) : value;
}

function formatRange(from: string, to: string): string {
  if (from === to) return formatDateValue(from);
  return `${formatDateValue(from)} - ${formatDateValue(to)}`;
}

function formatAccessibleDate(date: Date): string {
  return new Intl.DateTimeFormat("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}
